import { useCommonRouter } from '@repo/platform';
import { BoxButton, MainTitle, StickyBottomWrapper, useDialog } from '@repo/ui';
import { useAuthContext } from '@repo/user-session';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useFormValidation } from '../hooks/use-form-validation';
import { FormFieldMap } from '../types';
import { checkEmailValidation, checkPasswordConfirmValidation, checkPasswordValidation } from '../utils';

import { FormField } from './SignItems';

export const ForgotPassword = () => {
  const router = useCommonRouter();
  const { authService } = useAuthContext();
  const { showDialog } = useDialog();

  const { fields } = useFormValidation(['email']);
  const footerElement = document.getElementById('bznav-footer');

  const sendResetEmail = async () => {
    const isEmailValid = fields.email?.validate(checkEmailValidation) ?? false;
    if (!isEmailValid || !authService) return;

    try {
      await authService.signService.sendPasswordResetEmail(fields.email?.inputState.value ?? '');
      showDialog({
        title: '비밀번호 재설정 메일을 발송했어요.',
        description: '입력한 이메일로 비밀번호 재설정 메일을 발송했어요.\n메일을 확인해주세요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              router.push(authService.signPaths.signIn);
            }
          }
        }
      });
    } catch (error: any) {
      console.error('Failed to send password reset email:', error);
      showDialog({
        title: '존재하지 않는 이메일입니다.'
      });
    }
  };

  return (
    <>
      <MainTitle
        size={'medium'}
        title={'비밀번호 재설정을 진행할게요'}
        description={`가입되어 있는 이메일 주소를 입력해주세요.\n입력한 이메일로 비밀번호 재설정 메일을 발송할게요.`}
      />

      {fields.email && (
        <form>
          <FormField
            inputState={fields.email.inputState}
            onValueChange={fields.email.setValue}
            checkValidation={() => fields.email?.validate(checkEmailValidation) ?? false}
            onKeyupEnter={sendResetEmail}
            label="이메일"
            placeholder="example@email.com"
            type="email"
            autoComplete="off"
          />
        </form>
      )}

      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <BoxButton
              size={'large'}
              className={'w-full'}
              disabled={!fields.email?.inputState.value || !checkEmailValidation(fields.email?.inputState.value).isValid}
              onClick={sendResetEmail}
            >
              이메일 발송하기
            </BoxButton>
          </StickyBottomWrapper>,
          footerElement
        )}
    </>
  );
};

export const ResetPassword = () => {
  const allFields: ('password' | 'passwordConfirm')[] = ['password', 'passwordConfirm'];
  const fieldForm: Record<'password' | 'passwordConfirm', FormFieldMap> = {
    password: {
      label: '새로운 비밀번호',
      placeholder: '영문, 숫자, 특수문자 포함 8자 이상',
      autoComplete: 'new-password',
      validation: checkPasswordValidation
    },
    passwordConfirm: {
      label: '비밀번호 확인',
      placeholder: '비밀번호를 다시 한 번 입력해주세요',
      autoComplete: 'new-password',
      validation: checkPasswordConfirmValidation
    }
  };
  const router = useCommonRouter();
  const { showDialog } = useDialog();
  const { authService } = useAuthContext();
  const footerElement = document.getElementById('bznav-footer');

  const { fields } = useFormValidation(allFields);
  const isAllFieldsValid = allFields.every(
    (field) =>
      fields[field]?.inputState.value &&
      fieldForm[field].validation(fields[field]?.inputState.value, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined).isValid
  );

  const resetPassword = async () => {
    let isValid = true;
    allFields.forEach((field) => {
      const isFieldValid = fields[field]?.validate(fieldForm[field].validation, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined);
      if (!isFieldValid) isValid = false;
    });

    if (!isValid || !authService) return;

    try {
      await authService.signService.resetPassword(router.query?.code as string, fields.password?.inputState.value ?? '');
      showDialog({
        id: 'reset-password-success',
        title: '비밀번호가 재설정되었습니다.',
        description: '새로운 비밀번호로 로그인해주세요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              router.push(authService.signPaths.signIn);
            }
          }
        }
      });
    } catch (error: any) {
      showDialog({
        id: 'reset-password-error',
        title: '비밀번호 재설정을 실패했습니다.',
        description: error.status === 400 ? '이미 변경이 완료되었어요.' : '알 수 없는 에러입니다.'
      });
    }
  };

  useEffect(() => {
    if (router.isReady && !router.query?.code) {
      showDialog({
        id: 'reset-password-invalid-link',
        title: '비정상적인 접근입니다.',
        description: '비밀번호 재설정 링크가 유효하지 않습니다. 다시 시도해주세요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              router.back();
            }
          }
        }
      });
    }
  }, [router, router.isReady, router.query?.code, showDialog]);

  return (
    <>
      <MainTitle size={'medium'} title={'변경할 새로운\n비밀번호를 입력해주세요'} />
      <form>
        {allFields.map((field) => {
          if (fields[field]) {
            const filedFormItem = fieldForm[field];
            return (
              <FormField
                key={field}
                inputState={fields[field].inputState}
                onValueChange={fields[field].setValue}
                checkValidation={() =>
                  fields[field]?.validate(filedFormItem.validation, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined) ?? false
                }
                onKeyupEnter={resetPassword}
                label={filedFormItem.label}
                placeholder={filedFormItem.placeholder}
                type={'password'}
                autoComplete={filedFormItem.autoComplete}
              />
            );
          }
          return null;
        })}
      </form>

      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <BoxButton size={'large'} className={'w-full'} disabled={!isAllFieldsValid} onClick={resetPassword}>
              변경하기
            </BoxButton>
          </StickyBottomWrapper>,
          footerElement
        )}
    </>
  );
};
